import * as React from "react";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { CruiseRequestContextInput, ContextInput, CruiseFilterType } from "__generated__/api";
import { getBexApiContext } from "bernie-context";

export interface CruiseSearchFiltersInput {
  filterType?: CruiseFilterType;
  destinations?: string;
  cruiseLines?: string;
  ships?: string;
  departureLocations?: string;
  sailingVarieties?: string;
  promotionCode?: string;
}

export interface CruiseBexApiQueryInputs {
  cruiseSearchOptionsInput: {
    adultCount: number;
    childCount: number;
    limit?: number;
    sortBy?: string;
  };
  cruiseRequestContextInput?: CruiseRequestContextInput;
  cruiseSearchFiltersInput: CruiseSearchFiltersInput;
  context?: ContextInput;
}

export interface CruiseBexApiComponentProps {
  inputs: CruiseBexApiQueryInputs;
  view?: string;
  title?: string;
}

export interface CruiseSailingDealsExtraFlexInputs {
  promotionCode?: string;
  cruiseLines?: string;
}

interface CruiseBexApiWrapperProps {
  flexContext: ExtendedContextStore;
  view?: string;
  title?: string;
  additionalInputs?: CruiseSailingDealsExtraFlexInputs;
}

const filterFunction = (flexContext: ExtendedContextStore, additionalInputs: CruiseSailingDealsExtraFlexInputs) => {
  const { searchContext } = flexContext;
  const { pageType } = searchContext;

  switch (pageType) {
    case "Url-Mapping": {
      return {
        filterType: CruiseFilterType.NAME,
        ...additionalInputs,
      };
    }
    case "Cruise-Departure-Port": {
      return {
        filterType: CruiseFilterType.DEPARTURE,
        departureLocations: searchContext.cruiseDeparturePort.code,
      };
    }
    case "Travel-Guide-Cruise": {
      return {
        filterType: CruiseFilterType.DESTINATION,
        destinationNames: searchContext.cruiseDestination.code,
      };
    }
    case "Cruise-Line-Information": {
      return {
        filterType: CruiseFilterType.NAME,
        cruiseLines: searchContext.cruiseLine.code,
      };
    }
    case "Cruise-Ship-Information": {
      return {
        filterType: CruiseFilterType.SHIP,
        ships: searchContext.cruiseShip.code,
        cruiseLines: searchContext.cruiseLine.code,
      };
    }
    case "Cruise-Theme-Information": {
      if (searchContext.cruiseTheme.name === "River") {
        return {
          filterType: CruiseFilterType.THEME_RIVER,
          sailingVarieties: "river",
        };
      } else {
        return {
          filterType: CruiseFilterType.THEME_OCEAN,
          sailingVarieties: "ocean",
        };
      }
    }
    default: {
      return {};
    }
  }
};

/**
 * Used to wrap Shared UI or Bex API components relevant to Cruise Sailing Deals (components in the @shared-ui/cruise/sailing-deals package).
 * If you use the additionalInfo props, make sure that all the components using the same query are in sync and have the
 * same variables, else duplicate queries will be sent to the downstreams services.
 *
 * Based on the PropertyBexApiWrapper and TripsBexApiWrapper files.
 * See PropertyReviewsWrapper or PropertyReviewsHeadline for more examples.
 * @param Component
 * @param options
 * @constructor
 */

export const CruiseSailingDealsWrapper = <P extends { inputs: CruiseBexApiWrapperProps }>(
  WrappedComponent: React.FC
) => {
  // @ts-ignore
  const Component: React.FC<Omit<P, "inputs"> & CruiseBexApiWrapperProps> = ({
    flexContext,
    additionalInputs,
    view,
    title,
    ...props
  }) => {
    if (!additionalInputs) {
      additionalInputs = {};
    }

    const contextInputs: CruiseSearchFiltersInput = filterFunction(flexContext, additionalInputs);

    const wrapperInputs: CruiseBexApiQueryInputs = {
      cruiseSearchOptionsInput: {
        adultCount: 2,
        childCount: 0,
        limit: 0,
      },
      cruiseRequestContextInput: {},
      cruiseSearchFiltersInput: contextInputs,
      context: getBexApiContext(flexContext),
    };

    return <WrappedComponent inputs={wrapperInputs} {...props} view={view} customTitle={title} />;
  };

  return Component;
};
